import { Injectable, inject } from '@angular/core';

import {
    CookieName,
    CookieService,
    EventsService,
    NativeEventType,
    SharedFeaturesApiService,
    SimpleEvent,
    UserService,
    VanillaEventNames,
} from '@frontend/vanilla/core';
import { BehaviorSubject, Observable, filter, firstValueFrom, merge } from 'rxjs';

import { LoyaltyLoginValidation, ValidateLoyaltyLoginRequest } from './loyalty-login.models';

/**
 * @whatItDoes
 * This service is responsible for validating the loyalty login and providing the validation result.
 *
 * @description
 * Service for handling loyalty login validation.
 *
 * @stable
 */
@Injectable({
    providedIn: 'root',
})
export class LoyaltyLoginService {
    private readonly sharedFeaturesApiService = inject(SharedFeaturesApiService);
    private readonly userService = inject(UserService);
    private readonly cookieService = inject(CookieService);
    private readonly eventsService = inject(EventsService);

    private _loyaltyValidation: LoyaltyLoginValidation | undefined;

    constructor() {
        merge(
            this.eventsService.events.pipe(
                filter(
                    (e: SimpleEvent) =>
                        e?.eventName === VanillaEventNames.EVENT_SENT_TO_NATIVE &&
                        [NativeEventType.LOGOUT, NativeEventType.RESET_TERMINAL].includes(e?.data?.eventName),
                ),
            ),
        ).subscribe(() => {
            this.cookieService.remove(CookieName.LoyaltyModeValues);
            this.loyaltyValidation = undefined;
            this.loyaltyLoginValidationEvents.next(null);
        });
    }

    /**
     * The current loyalty login validation object.
     */
    get loyaltyValidation(): LoyaltyLoginValidation | undefined {
        return this._loyaltyValidation || this.cookieService.getObject(CookieName.LoyaltyModeValues);
    }

    private set loyaltyValidation(value) {
        this.cookieService.putObject(CookieName.LoyaltyModeValues, value);
        this._loyaltyValidation = value;
    }

    private readonly loyaltyLoginValidationEvents = new BehaviorSubject<LoyaltyLoginValidation | null>(null);

    /**
     * Allows to subscribe to loyalty login validation values.
     */
    get loyaltyLoginValidation(): Observable<LoyaltyLoginValidation | null> {
        return this.loyaltyLoginValidationEvents;
    }

    /**
     * Validates the loyalty login.
     *
     * @param request - The request object containing the loyalty login data.
     * @returns A promise that resolves to the loyalty login validation object or null if already loaded.
     */
    async validate(request: ValidateLoyaltyLoginRequest): Promise<LoyaltyLoginValidation | null> {
        if (!this.loyaltyValidation && this.userService.isAuthenticated) {
            const response = await firstValueFrom(this.sharedFeaturesApiService.post('loyaltylogin/validate', request));
            this.loyaltyValidation = response;
            this.loyaltyLoginValidationEvents.next(response);

            return response;
        }

        return null;
    }
}
